import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail, ChevronsDown, Sparkle } from "lucide-react";
import { api, formatIDR, formatDuration } from "@/lib/api";
import { TalentCard, Disclaimer } from "@/components/shared";
import { FeaturedSlider } from "@/components/FeaturedSlider";

const HERO_IMG = "https://images.unsplash.com/photo-1758600587839-56ba05596c69?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FEATURE_IMG = "https://images.unsplash.com/photo-1567708619745-627922a51d98?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const SERVICE_KIND = { curhat: "Telepon", sleepcall: "Panggilan malam", chat: "Teks" };
const STEPS = [
  ["Pilih layanan & talent", "Lihat profil, keahlian, dan rating. Semua talent sudah kami wawancara."],
  ["Isi nama panggilan & WhatsApp", "Tanpa daftar akun. Nomor kamu hanya terlihat talent selama order aktif."],
  ["Bayar QRIS, talent menghubungi", "Pembayaran terkonfirmasi otomatis. Talent menerima dalam 10 menit atau order dialihkan."],
  ["Bercerita, lalu beri rating", "Sesi berjalan dengan timer. Setelah selesai, nomor kamu disamarkan."],
];

function Tag({ color, children, glass = false }) {
  return <span className={glass ? "glass inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm text-neutral-900" : "tag"}><span className="tag-dot" style={{ background: color }} />{children}</span>;
}

function Dots({ className = "" }) {
  return <span className={`flex gap-1.5 ${className}`} aria-hidden="true"><span className="w-3.5 h-3.5 rounded-full bg-white/70" /><span className="w-3.5 h-3.5 rounded-full bg-white/70" /></span>;
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [talents, setTalents] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/talents").then((r) => setTalents(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <section className="pt-10 pb-16 lg:pt-14 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] items-stretch">
        <div className="fade-up flex flex-col">
          <div className="flex flex-wrap gap-2"><Tag color="var(--mint)">Curhat</Tag><Tag color="var(--peach)">Sleepcall</Tag><Tag color="#428CCB">Chat Buddy</Tag></div>
          <h1 className="display mt-8 text-[44px] sm:text-6xl lg:text-[64px] text-neutral-900" data-testid="hero-title">
            <span className="block">Tempat</span>
            <span className="inline-block mt-2 px-4 py-1 rounded-full border border-[var(--peach)]">Ceritamu</span>
            <span className="flex items-center gap-3 mt-2"><Sparkle size={34} className="text-[#7FD9B3] fill-[#B9F1DA] shrink-0" strokeWidth={1.5} />Didengar</span>
          </h1>
          <hr className="mt-8 border-neutral-200" />
          <p className="mt-6 text-base text-neutral-600 leading-relaxed max-w-sm">Kamu berhak didengarkan tanpa dihakimi. Di Sudut Nadi, ceritamu akan didengarkan oleh talent yang telah kami verifikasi dan kami kurasi.</p>
          <div className="mt-8 flex flex-wrap gap-2">
            <Link to="/talent" className="btn-primary" data-testid="hero-cta-talent">Pilih Talent</Link>
            <Link to="/cek-order" className="btn-outline" data-testid="hero-cta-cek">Cek Order</Link>
          </div>
          <div className="mt-auto pt-10 flex gap-3">
            <a href="mailto:halo@sudutnadi.id" className="circle-btn" aria-label="Email" data-testid="hero-mail"><Mail size={22} strokeWidth={1.5} /></a>
            <a href="#layanan" className="circle-btn" aria-label="Gulir ke bawah" data-testid="hero-scroll"><ChevronsDown size={22} strokeWidth={1.5} /></a>
          </div>
        </div>

        <div className="photo-card min-h-[420px] lg:min-h-[620px]" data-testid="hero-photo">
          <img src={HERO_IMG} alt="Talent Sudut Nadi tersenyum saat mendengarkan" className="absolute inset-0 w-full h-full object-cover object-top" />
          <Dots className="absolute top-6 right-6" />
          <div className="absolute bottom-24 right-6 glass rounded-2xl p-5 w-[min(260px,70%)] text-neutral-900">
            <div className="flex items-start justify-between"><span className="font-heading text-4xl font-semibold">100%</span><ArrowUpRight size={20} strokeWidth={1.5} /></div>
            <p className="mt-2 text-sm leading-snug text-neutral-800">Direspon oleh Real Human, bukan AI.</p>
          </div>
          <div className="absolute left-6 right-6 bottom-6 flex flex-wrap gap-2">
            <Tag glass color="var(--mint)">Didengar tanpa dihakimi</Tag>
            <Tag glass color="var(--peach)">Privasi terjaga</Tag>
          </div>
          <Dots className="absolute left-6 top-24 flex-col" />
        </div>
      </section>

      <section id="layanan" className="py-16 lg:py-24 grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-center scroll-mt-24">
        <div>
          <p className="eyebrow mb-6">Layanan</p>
          <ul className="divide-y divide-neutral-300">
            {services.map((s) => (
              <li key={s.id}>
                <Link to={`/talent?service=${s.id}`} className="group flex items-start gap-5 py-7" data-testid={`service-card-${s.id}`}>
                  <span className="mt-2 grid grid-cols-2 gap-1 shrink-0" aria-hidden="true"><i className="w-2 h-2 rounded-full bg-neutral-800" /><i className="w-2 h-2 rounded-full bg-neutral-300" /><i className="w-2 h-2 rounded-full bg-neutral-300" /><i className="w-2 h-2 rounded-full bg-neutral-800" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-baseline gap-2 flex-wrap"><span className="font-heading text-3xl font-medium text-neutral-900">{s.name}</span><span className="text-neutral-500">({SERVICE_KIND[s.id] || "Layanan"})</span></span>
                    <span className="block mt-1.5 text-sm text-neutral-600">{s.tagline} Mulai {formatIDR(s.packages[0].price)} / {formatDuration(s.packages[0].minutes)}.</span>
                  </span>
                  <ArrowUpRight size={34} strokeWidth={1.5} className="shrink-0 text-neutral-900 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid gap-8 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] items-center">
          <FeaturedSlider fallbackImg={FEATURE_IMG} className="aspect-[4/5]" />
          <div className="sm:text-right">
            <div className="flex flex-wrap gap-2 sm:justify-end"><Tag color="var(--mint)">Terverifikasi</Tag><Tag color="var(--peach)">Dilatih</Tag></div>
            <h2 className="display mt-6 text-4xl sm:text-5xl text-neutral-900">Kenapa harus Sudut Nadi?</h2>
            <p className="mt-5 text-neutral-600 leading-relaxed"><span className="block">Setiap talent di Sudut Nadi telah kami wawancara dan verifikasi.</span><span className="block mt-2">Talent menggunakan WA Business khusus, bukan nomor pribadi.</span><span className="block mt-2">Talent telah dibekali protokol krisis.</span></p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-4">Talent</p>
            <h2 className="display text-4xl sm:text-5xl text-neutral-900">Sedang siap<br />mendengarkan</h2>
          </div>
          <Link to="/talent" className="btn-outline" data-testid="home-see-all-talent">Lihat semua <ArrowRight size={16} strokeWidth={1.5} /></Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {talents.slice(0, 4).map((t) => <TalentCard key={t.id} t={t} />)}
        </div>
      </section>

      <section className="py-16 lg:py-24 border-t border-neutral-200">
        <p className="eyebrow mb-4">Cara kerja</p>
        <h2 className="display text-4xl sm:text-5xl text-neutral-900">Empat langkah mudah</h2>
        <ol className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([title, desc], i) => (
            <li key={title}>
              <span className="font-heading text-5xl font-semibold text-neutral-200">0{i + 1}</span>
              <h3 className="mt-4 font-heading text-xl font-medium text-neutral-900">{title}</h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{desc}</p>
            </li>
          ))}
        </ol>
        <div className="mt-16"><Disclaimer /></div>
      </section>
    </div>
  );
}
